import { select } from "@/utils";
import { Expandable, ExpandableDelegate } from "@/expandable";

export interface TreeItemSettings {
    /** Class applied to the item when it is highlighted. Only applies in single selection mode, to the deepest selected item. */
    highlightedClass: string;
    /** Class identifying the button or checkbox used to select/deselect the item */
    toggleClass: string;
}


export interface TreeItemDelegate {
    /** Transitions the item to the specified selected/deselected state. */
    transitionToSelectedState: (item: TreeItem, selected: boolean) => void;
    /** Transitions the item to the specified expanded/collapsed state. */
    transitionToExpandedState: (item: TreeItem, expanded: boolean) => void;
}

interface TreeItemToggleDelegate {
    transitionToSelectedState(selected: boolean): void;
}

interface TreeItemToggle {
    selected: boolean;
    connect(delegate: TreeItemToggleDelegate): void;
    disconnect(): void;
}

class CheckboxToggle implements TreeItemToggle {

    private delegate: TreeItemToggleDelegate | null = null;

    constructor(
        private checkbox: HTMLInputElement
    ) {}

    get selected() {
        return this.checkbox.checked;
    }

    set selected(value: boolean) {
        this.checkbox.checked = value;
    }

    connect(delegate: TreeItemToggleDelegate) {
        this.delegate = delegate;
        this.checkbox.addEventListener('change', this.onCheckboxChange);
    }

    disconnect(): void {
        this.delegate = null;
        this.checkbox.removeEventListener('change', this.onCheckboxChange);
    }

    private onCheckboxChange = () => {
        this.delegate?.transitionToSelectedState(this.checkbox.checked);
    }
}

class ButtonToggle implements TreeItemToggle {
    private delegate: TreeItemToggleDelegate | null = null;

    constructor(
        private button: HTMLElement,
    ) {}

    get selected() {
        return this.button.ariaPressed === 'true';
    }

    set selected(value: boolean) {
        this.button.ariaPressed = value ? 'true' : 'false';
    }

    connect(delegate: TreeItemToggleDelegate) {
        this.delegate = delegate;
        this.button.addEventListener('click', this.onButtonClick);
    }

    disconnect() {
        this.delegate = null;
        this.button.removeEventListener('click', this.onButtonClick);
    }

    private onButtonClick = () => {
        this.selected = !this.selected;
        this.delegate?.transitionToSelectedState(this.selected);
    }
}

/** 
 * A selectable tree item, which will be expandable if it has subitems. 
 * 
 * Note that the item does not directly select/deselect itself in response to user interaction, but simply notifies a higher level component when such interaction occurs. This is important because another item may need to be deselected before this one can be selected (when limiting selection to a single item), or content may be need to be loaded via AJAX before expanding the item.
 **/
export class TreeItem implements ExpandableDelegate, TreeItemToggleDelegate {

    public toggle?: TreeItemToggle;
    private delegate: TreeItemDelegate | null = null;

    /**
     * @param list Exposed as a public property so higher level components can distinguish whether or not this item is expandable, and directly expand/collapse it as needed based on the selection mode. Only defined for items that are expandable.
     */
    constructor(
        public readonly element: HTMLElement,
        private readonly settings: TreeItemSettings,
        public readonly list?: Expandable,
    ) {}

    /** Checks if this item is currently highlighted (used for single-select rendering) */
    get highlighted(): boolean {
        return this.element.classList.contains(this.settings.highlightedClass);
    }

    /** Programmatically sets the highlight style (single-select visual state only) */
    set highlighted(selected: boolean) {
        this.element.classList.toggle(this.settings.highlightedClass, selected);
    }

    /** Checks if this item is selected */
    get selected(): boolean {
        return this.toggle?.selected ?? false;
    }

    /** Programatically selects or deselects this item, triggering any higher level selection logic. No-op if already in the desired state. Warning: Higher level components should generally not use this function directly, since it triggers callbacks; instead, they should directly alter the item state (toggle.selected, highlighted) */
    set selected(value: boolean) {
        if (this.toggle && this.delegate && value !== this.selected) {
            this.toggle.selected = value;
            this.delegate.transitionToSelectedState(this, value);
        }
    }

    /** Gets the value of this item (similar to option value in a select element) */
    get value(): string {
        return this.element.dataset.value!;
    }   

    connect(delegate: TreeItemDelegate): void {
        this.delegate = delegate;

        // Set up sublist expand/collapse behavior
        this.list?.connect(this);

        const isSelectable = this.element.dataset.selectable !== 'false';
        // Toggle is optional only for non-selectable subselect levels in multi mode.
        const toggle = select(this.element, {class: this.settings.toggleClass, required: isSelectable});
        if (!toggle) {
            this.toggle = undefined;
            return;
        }

        // Set up the toggle button/checkbox to fire selection events in a way that's appropriate for the control type
        const isCheckbox = toggle instanceof HTMLInputElement && toggle.type === 'checkbox';
        this.toggle = isCheckbox ? new CheckboxToggle(toggle) : new ButtonToggle(toggle);
        this.toggle.connect(this);
    }

    disconnect() {
        this.delegate = null;
        this.toggle?.disconnect();
        this.list?.disconnect();
    }

    // Enrich callbacks with full tree item context
    transitionToSelectedState(selected: boolean): void {
        this.delegate?.transitionToSelectedState(this, selected);
    }

    transitionToExpandedState(list: Expandable, expanded: boolean): void {
        this.delegate?.transitionToExpandedState(this, expanded);
    }
}
